import { useMemo, useState, useEffect, useRef } from 'react'
import { useSearch } from '@tanstack/react-router'
import { CalendarDays } from '@/components/icons'
import dayjs, { Dayjs } from 'dayjs'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { DutyStatus } from '../../../../api/src/shared'
import { useRosterData, type TripLegData } from '../../lib/hooks/useRosterData'
import { useAircraftMaintenance } from '../../lib/hooks'
import { MaintenanceDayList } from '../MaintenanceDayList'
import type { RosterFilters } from '../RosterFilterDrawer'
import type { SelectedRosterEntry } from '../RosterGanttView'
import { formatDutyStatus } from '../../utils/statusColors'
import { RosterListEntryItem, type TripLegInfo } from '../RosterListEntryItem'
import { EmptyState } from '../EmptyState'
import { RosterContentSkeleton } from '../RosterKpiCard'

export type RosterListMode = 'me' | 'team'

interface RosterListViewProps {
  onEntryClick?: (entry: SelectedRosterEntry) => void
  filters?: RosterFilters
  /** 'me' shows duty status as title, 'team' shows crew name (default: 'team') */
  mode?: RosterListMode
  className?: string
}

interface RosterEntryWithCrewName {
  id: string
  date: Dayjs
  crewId: string
  dutyStatus: DutyStatus
  crewName: string
  tripLegs?: TripLegData[]
  hasNote?: boolean
}

interface DateGroup {
  date: Dayjs
  entries: RosterEntryWithCrewName[]
}

// Extract trip leg details for display in list view
function getTripLegInfo(entry: RosterEntryWithCrewName): TripLegInfo | null {
  if (!entry.tripLegs || entry.tripLegs.length === 0) {
    return null
  }

  const firstLeg = entry.tripLegs[0]
  const lastLeg = entry.tripLegs[entry.tripLegs.length - 1]

  const departure = firstLeg.departureAirportIcao || ''
  const arrival = lastLeg.arrivalAirportIcao || ''
  const aircraft = firstLeg.aircraft?.registration || ''

  if (!departure) {
    return null
  }

  return { departure, arrival, aircraft }
}

export function RosterListView({
  onEntryClick,
  filters,
  mode = 'team',
  className,
}: RosterListViewProps) {
  const search = useSearch({ strict: false }) as { selectedMonth?: string }
  const scrollRef = useRef<HTMLDivElement>(null)
  const todayRef = useRef<HTMLDivElement>(null)
  const hasScrolledRef = useRef(false)
  const todayKey = dayjs().format('YYYY-MM-DD')

  // Initialize from URL param if available, otherwise use current month
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (search.selectedMonth) {
      const parsed = dayjs(search.selectedMonth, 'YYYY-MM')
      if (parsed.isValid()) {
        return parsed
      }
    }
    return dayjs()
  })

  // Sync with URL param when it changes (driven by the shared date nav in
  // CrewRoster). Reset the scroll flag so we re-scroll to today on month change.
  useEffect(() => {
    if (search.selectedMonth) {
      const parsed = dayjs(search.selectedMonth, 'YYYY-MM')
      if (parsed.isValid() && !parsed.isSame(currentMonth, 'month')) {
        hasScrolledRef.current = false
        setCurrentMonth(parsed)
      }
    }
  }, [search.selectedMonth])

  // Calculate date range for current month
  const monthStart = currentMonth.startOf('month').format('YYYY-MM-DD')
  const monthEnd = currentMonth.endOf('month').format('YYYY-MM-DD')

  // Use shared roster data hook
  const {
    rosterEntries,
    crewMembers,
    crewTripLegMap,
    isLoading,
  } = useRosterData({
    startDate: monthStart,
    endDate: monthEnd,
    aircraftIds: filters?.aircraftIds,
  })

  // Maintenance lookup: selected aircraft, or all aircraft when none selected
  const { getMaintenanceForDay } = useAircraftMaintenance(filters?.aircraftIds)

  // Group roster entries by date, applying filters
  const entriesGroupedByDate = useMemo(() => {
    const groups: Record<string, DateGroup> = {}

    rosterEntries.forEach((entry) => {
      // Apply crew filter
      if (filters?.crewMemberIds?.length && !filters.crewMemberIds.includes(entry.crewId)) {
        return
      }
      // Apply duty status filter
      if (filters?.dutyStatuses?.length && !filters.dutyStatuses.includes(entry.dutyStatus)) {
        return
      }

      const crew = crewMembers.find((c) => c.id === entry.crewId)
      const crewName = crew?.name || 'Unknown Crew'
      const dateKey = entry.date.format('YYYY-MM-DD')

      if (!groups[dateKey]) {
        groups[dateKey] = { date: entry.date, entries: [] }
      }

      // Get trip leg data if available
      const tripLegs = crewTripLegMap[entry.crewId]?.[dateKey]

      groups[dateKey].entries.push({
        id: entry.id,
        date: entry.date,
        crewId: entry.crewId,
        dutyStatus: entry.dutyStatus,
        crewName,
        tripLegs,
        hasNote: !!entry.note,
      })
    })

    // Sort by date and sort entries within each group by crew name
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, group]) => {
        group.entries.sort((a, b) => a.crewName.localeCompare(b.crewName))
        return group
      })
  }, [rosterEntries, crewMembers, crewTripLegMap, filters?.crewMemberIds, filters?.dutyStatuses])

  // Scroll to today's date group on mount / when current month is viewed
  useEffect(() => {
    if (
      isLoading ||
      hasScrolledRef.current ||
      entriesGroupedByDate.length === 0
    ) {
      return
    }
    if (!currentMonth.isSame(dayjs(), 'month')) {
      // Non-current months stay scrolled to the top
      hasScrolledRef.current = true
      return
    }
    if (todayRef.current && scrollRef.current) {
      const top = todayRef.current.offsetTop - scrollRef.current.offsetTop
      scrollRef.current.scrollTop = Math.max(0, top)
    }
    hasScrolledRef.current = true
  }, [isLoading, entriesGroupedByDate, currentMonth])

  return (
    <div className={cn('w-full flex flex-col gap-4 min-h-0', className)}>
      {/* Entries grouped by date */}
      {isLoading ? (
        <RosterContentSkeleton className="flex-1" />
      ) : entriesGroupedByDate.length === 0 ? (
        <div className="flex-1 flex flex-col">
          <EmptyState
            icon={CalendarDays}
            title="No roster entries"
            description="No roster entries for this month"
          />
        </div>
      ) : (
        <Card ref={scrollRef} className="w-full px-4 py-4 flex-1 min-h-0 overflow-y-auto">
          <div className="flex flex-col gap-4">
            {entriesGroupedByDate.map((group) => {
              const dateKey = group.date.format('YYYY-MM-DD')
              const isToday = dateKey === todayKey
              return (
              <div
                key={dateKey}
                ref={isToday ? todayRef : undefined}
                className="flex flex-col gap-4"
              >
                {/* Date Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-content-primary-bold tracking-[0.07px]">
                      {group.date.format('MMM D')}
                    </p>
                  </div>
                </div>

                {/* Aircraft in maintenance for this day */}
                <MaintenanceDayList aircraft={getMaintenanceForDay(group.date)} />

                {/* Entries for this date */}
                <div className="flex flex-col gap-4">
                  {group.entries.map((entry, index) => {
                    const crew = crewMembers.find((c) => c.id === entry.crewId)
                    const tripLeg = getTripLegInfo(entry)
                    const Wrapper = onEntryClick ? 'button' : 'div'
                    return (
                      <Wrapper
                        key={`${entry.id}-${index}`}
                        type={onEntryClick ? 'button' : undefined}
                        className="w-full"
                        onClick={onEntryClick ? () => {
                          onEntryClick({
                            entryId: entry.id,
                            crewId: entry.crewId,
                            date: entry.date.toDate(),
                            dutyStatus: entry.dutyStatus,
                            crew: crew ? {
                              id: crew.id,
                              name: crew.name,
                              firstName: crew.firstName,
                              lastName: crew.lastName,
                              avatarColor: crew.avatarColor,
                            } : undefined,
                          })
                        } : undefined}
                      >
                        <RosterListEntryItem
                          title={mode === 'me' ? formatDutyStatus(entry.dutyStatus) : entry.crewName}
                          dutyStatus={entry.dutyStatus}
                          tripLeg={tripLeg}
                          hasNote={entry.hasNote}
                        />
                      </Wrapper>
                    )
                  })}
                </div>
              </div>
              )
            })}
          </div>
        </Card>
      )}
    </div>
  )
}
